import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";
import Config from "../../config/Config.js";

export default function PersonalChange() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const isValidEmail = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!firstName.trim() || !lastName.trim() || !isValidEmail(email)) {
      setFeedback({
        type: "error",
        message: "Please fill out all fields with valid information.",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(
        `${Config().userUrl}${user.pk}/`,
        {
          username: user.username,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
        },
        {
          headers: {
            Authorization: `Token ${auth.token}`,
          },
        }
      );

      // Persist in localStorage
      localStorage.setItem("USER_EMAIL", res.data.email);
      localStorage.setItem("USER_FIRST_NAME", res.data.first_name);
      localStorage.setItem("USER_LAST_NAME", res.data.last_name);

      setFeedback({ type: "success", message: "Changes saved successfully." });
    } catch (err) {
      console.error("Personal Info Change Error:", err);
      console.error("Personal Info Change Error Response:", err.response);
      console.error("Personal Info Change Error Status:", err.response?.status);
      console.error("Personal Info Change Error Data:", err.response?.data);
      
      let errorMessage = "Failed to save changes. Please try again.";
      if (err.response?.status === 400) {
        errorMessage = "Invalid personal information provided.";
        if (err.response?.data?.email) {
          errorMessage = "This email address is already in use or invalid.";
        }
      } else if (err.response?.status === 403) {
        errorMessage = "You don't have permission to update this information.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error occurred while saving personal information.";
      }
      
      setFeedback({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings__card">
      <h2 className="settings__formTitle">Personal Information</h2>
      <p className="settings__formSubtitle">
        Update your first name, last name, and email address.
      </p>
      <hr />

      {feedback.message && (
        <div
          className={
            feedback.type === "success"
              ? "form-alert form-alert--success"
              : "form-alert form-alert--error"
          }
        >
          {feedback.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings__form">
        <div className="form-group">
          <label htmlFor="firstName" className="form-label">
            First name
          </label>
          <input
            id="firstName"
            type="text"
            className="form-control auth-form"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName" className="form-label">
            Last name
          </label>
          <input
            id="lastName"
            type="text"
            className="form-control auth-form"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="form-control auth-form"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-brand"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
